import type { ImgHTMLAttributes } from 'react';

import iconColor from '../../assets/bia-icon-color.svg';
import iconMonoDark from '../../assets/bia-icon-mono-dark.svg';
import iconMonoWhite from '../../assets/bia-icon-mono-white.svg';
import iconWhite from '../../assets/bia-icon-white.svg';
import smallColor from '../../assets/bia-logo-small-color.svg';
import smallMonoDark from '../../assets/bia-logo-small-mono-dark.svg';
import smallMonoWhite from '../../assets/bia-logo-small-mono-white.svg';
import smallWhite from '../../assets/bia-logo-small-white.svg';
import { cn } from '../lib/cn';

const SOURCES = {
  small: {
    color: smallColor,
    white: smallWhite,
    'mono-white': smallMonoWhite,
    'mono-dark': smallMonoDark,
  },
  icon: {
    color: iconColor,
    white: iconWhite,
    'mono-white': iconMonoWhite,
    'mono-dark': iconMonoDark,
  },
} as const;

export interface BrandLogoProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  /** `small` = lockup for headers/login/sidebar (min 72px); `icon` = mark alone (min 16px). */
  tier?: keyof typeof SOURCES;
  /**
   * `color` on light backgrounds (default) · `white` on navy/dark · `mono-white`
   * on photos/color fills · `mono-dark` for greyscale. Never place the gradient
   * mark (`color`/`white`) on a green or cyan fill (docs/15 §3).
   */
  variant?: keyof (typeof SOURCES)['small'];
  /** Required: the runtime brand name (ADR 0015 — text branding is env-driven, so
   * the package must not bake in a default announcement for screen readers). */
  alt: string;
}

/**
 * The only sanctioned way to render the Bia mark (docs/15 §3): true SVG assets
 * from the brand kit — never recreated, recolored, cropped, or filtered.
 */
export function BrandLogo({
  tier = 'small',
  variant = 'color',
  alt,
  className,
  ...props
}: BrandLogoProps) {
  return (
    <img
      src={SOURCES[tier][variant]}
      alt={alt}
      className={cn(tier === 'small' ? 'h-8 w-auto min-w-[72px]' : 'h-6 w-6', className)}
      {...props}
    />
  );
}
